import React from "react";
import { motion } from "framer-motion";
import { LayoutGrid, FileText, ShieldCheck, Gauge } from "lucide-react";
import LogoMark, { LOGO_URL } from "./LogoMark";

const HIGHLIGHTS = [
  { icon: FileText, text: "Create and track Proforma Invoices" },
  { icon: LayoutGrid, text: "Centralized product & customer master data" },
  { icon: Gauge, text: "Real-time quotation status across your team" },
  { icon: ShieldCheck, text: "Role-based access for admins & partners" },
];

/**
 * Split-screen shell shared by Login/Signup/ForgotPassword - a branding
 * panel on the left, the auth form on the right. Standard pattern for
 * enterprise/business software (vs. a plain centered card on a blank page).
 */
const AuthLayout = ({ children, formWidth = "max-w-md" }) => {
  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Branding panel */}
      <div className="hidden lg:flex lg:w-[42%] bg-ink-950 text-white flex-col justify-between p-10 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px, transparent 1px)",
            backgroundSize: "28px 28px",
          }}
        />
        <div className="relative">
          <div className="inline-flex items-center gap-2.5">
            <LogoMark size={38} />
            <span className="text-lg font-semibold text-white tracking-tight">Landschaft</span>
          </div>
          <p className="text-ink-300 text-xs mt-2 tracking-wide uppercase">Quotation &amp; PI Management</p>
        </div>

        <div className="relative">
          <h2 className="text-3xl font-semibold leading-tight text-white">
            Run your Proforma Invoice<br />workflow in one place.
          </h2>
          <p className="text-ink-400 text-sm mt-3 max-w-sm">
            From product catalog to customer records to a finished PI - built for
            teams who need speed and accuracy on every quotation.
          </p>

          <ul className="mt-8 space-y-3">
            {HIGHLIGHTS.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-center gap-3 text-sm text-ink-100">
                <span className="h-8 w-8 rounded-md bg-white/10 flex items-center justify-center shrink-0">
                  <Icon size={15} />
                </span>
                {text}
              </li>
            ))}
          </ul>
        </div>

        <p className="relative text-xs text-ink-500">© {new Date().getFullYear()} Landschaft. All rights reserved.</p>
      </div>

      {/* Form panel */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className={`w-full ${formWidth}`}
        >
          {/* Mobile-only brand mark */}
          <div className="lg:hidden flex items-center justify-center mb-8">
            <img src={LOGO_URL} alt="Landschaft" className="h-9 w-auto object-contain" />
          </div>
          {children}
        </motion.div>
      </div>
    </div>
  );
};

export default AuthLayout;
